<template>
  <div id="server-status">
    <div class="container">

      <Panel :title="$t('advanced_navigation.servers_status')">
        <div class="backend">
          <label>Current Backend Version</label>
          <div class="version">{{ backendVersion }}</div>
        </div>
        <br>

        <Panel title="Versions" v-if="!!versionData.rows.length" collapsible>
          <RawTable first-row-bold :columns="versionData.columns" :rows="versionData.rows" />
        </Panel>
        <Panel title="Configured urls" v-if="!!urlData.rows.length" collapsible>
          <RawTable first-row-bold :columns="urlData.columns" :rows="urlData.rows" />
        </Panel>
      </Panel>

      <Panel title="Custom Watched Resources" v-if="watchedResources">
        <Panel :title="section.title" v-for="(section, sectionIndex) of watchedResources" :key="`sec_${sectionIndex}`"
          collapsible>
          <table class="section-table raw-table-report">
            <thead>
              <tr>
                <th class="status-th">{{ $t("pos.status") }}</th>
                <th class="version-th">{{ $t("version") }}</th>
                <th class="url-th">URL</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(resource, resourceIndex) of section.resources"
                :key="`sec_${sectionIndex}_res_${resourceIndex}`">
                <td class="status-td">
                  <div class="circle" :style="{ backgroundColor: pillColor(resource.reachable) }"></div>
                </td>
                <td class="version-td">
                  <div class="version">
                    <template v-if="resource.error">
                      <Tooltip :text="resource.error" class="tooltip" />
                    </template>
                    <template v-else>{{ resource.version || "N/A" }}</template>
                  </div>
                </td>
                <td class="url-td">{{ resource.url }}</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </Panel>

    </div>
  </div>
</template>

<script>
  import { Core } from "@/core/Core";
  import RawTable from "@/components/RawTable";
  import Vue from "vue";

  export default {
    name: "ServerStatus",
    components: { RawTable },

    data() {
      return {
        watchedResources: null,
        backendVersion: null,
        versionData: {
          rows: [],
          columns: [
            { en: 'Server' },
            { en: 'API' },
            { en: 'Frontend' }
          ]
        },
        urlData: {
          rows: [],
          columns: [
            { en: 'Server' },
            { en: 'API' },
            { en: 'Frontend' }
          ],
        },
      }
    },

    computed: {
      accessibleServers() {
        return this.$store.state.loggedUser.accessibleServers || []
      }
    },

    watch: {
      accessibleServers: {
        immediate: true,
        async handler(servers) {
          if (!servers || servers.length === 0) {
            return;
          }

          showSpinner();
          let results = await Promise.allSettled(
            servers.map(s => Core.request('GET', '/api/server_versions', undefined, { baseUrl: s.url }))
          );
          hideSpinner();

          this.urlData.rows = [];
          this.versionData.rows = [];
          for (let [index, server] of Object.entries(servers)) {
            let fulfilled = results[index]['status'] === 'fulfilled';
            let result = fulfilled ? results[index]['value'] : {};

            if (result.frontend.version.indexOf("error") > -1) {
              result.frontend.version = "Unknown";
            }

            this.urlData.rows.push([
              server.name,
              server.url,
              fulfilled ? (result.frontend.url || 'Unknown') : 'error'
            ]);

            this.versionData.rows.push([
              server.name,
              fulfilled ? (result.api.version || 'not found') : 'error',
              fulfilled ? (result.frontend.version || 'Unknown') : 'error'
            ]);

            if (!fulfilled) {
              toast(`Error on fetching server '${server.name}'. Status: ${results[index]['reason']['status']}`, 'error')
            }
          }
        }
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "advanced_navigation.servers_status";
      this.$store.state.pageIcon = "";

      this.getLocalBackendVersion();

      /*
      * Parameters.watchedResources
      * [{
      *   title: string,
      *   resources: [{
      *     type: app|api|stream|tunnel,
      *     url: string
      *   }]
      * }]
      * */
      if (Parameters.watchedResources) {
        this.watchedResources = JSON.parse(JSON.stringify(Parameters.watchedResources));
      }

      /* compute watched resources reachability and version */
      for (let section of this.watchedResources || []) {
        let responses = await Promise.allSettled(section.resources.map(this.fetchResourceStatusPromise));
        for (let [index, r] of Object.entries(responses)) {
          let reachable = r.status === 'fulfilled';
          Vue.set(section.resources[index], 'reachable', reachable);

          if (r.status === 'fulfilled' && r.value && r.value.version) {
            Vue.set(section.resources[index], 'version', r.value.version);
          }
          if (r.status === 'rejected') {
            let reason = r.reason;
            let error = '???';
            if (reason instanceof XMLHttpRequest) {
              error = reason.responseText
            } else if (typeof reason === 'object' && reason.message) {
              error = reason.message
            } else if (typeof reason === 'string') {
              error = reason
            }
            Vue.set(section.resources[index], 'error', error);
          }
          if (r.status === 'rejected') {
            console.log(r)
          }
        }
      }
    },

    methods: {
      pillColor(bool) {
        if (bool === true) {
          return '#208228';
        } else if (bool === false) {
          return '#ce1a0d';
        } else {
          return '#c3c3c3';
        }
      },
      async getLocalBackendVersion() {
        this.backendVersion = await this.fetchContent('', "version.txt");
      },
      fetchResourceStatusPromise(resource) {
        let type = resource.type;
        let baseUrl = resource.url;
        if (type === 'api') {
          return this.pingDirectly(baseUrl, '/api/status?version=1')
        } else if (type === 'stream') {
          // cannot ping directly because cors headers are not sent
          return this.pingViaProxy(baseUrl, '/getStatus')
        } else if (type === 'app') {
          //cannot ping directly because cors problems with the backend app
          return this.pingViaProxy(baseUrl, '/version.txt').then(txt => ({ version: txt }))
        } else if (type === 'tunnel') {
          return Promise.reject('NOT IMPLEMENTED')
        } else {
          return Promise.reject(`UNKNOWN RESOURCE TYPE '${type}'`)
        }
      },
      fetchContent(baseUrl, path) {
        return fetch(`${baseUrl}${path}`).then(async r => await r.text())
      },
      pingDirectly(baseUrl, path) {
        return Core.request('GET', path, null, { baseUrl, ignoreToken: true });
      },
      pingViaProxy(baseUrl, path) {
        /* avoid cors problem */
        return Core.request('GET', '/api/proxy_service', { url: baseUrl + path })
      }
    }
  }
</script>

<style lang="scss" scoped>
  .backend {
    label {
      margin-bottom: 2px;
    }

    .version {
      font-weight: 500;
    }
  }

  .section-table {
    width: 100%;

    th,
    td {
      text-align: left;
    }

    .version-th {
      width: 120px;
    }

    .status-th {
      width: 120px;
    }

    .version {
      font-weight: 500;
    }

    .circle {
      border-radius: 100%;
      $size: 20px;
      width: $size;
      height: $size;
      margin-left: 5px;
      display: inline-block;
    }
  }
</style>